import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, BanIcon, BarChart3Icon, BriefcaseIcon, CheckCircle2Icon, InfoIcon, LayersIcon, Loader2Icon, RouteIcon, UserIcon } from 'lucide-react';
import { ownerModeOptions, profiles, queues, reassignModeOptions } from '../../data/offboarding';
import { neutralButton, primaryButton } from '../../utils/buttonStyles';
import { ConfirmModal } from './ConfirmModal';
import { PillMultiSelect } from './PillMultiSelect';
import type { PillOption } from './PillMultiSelect';
import { PreviewPanel } from './PreviewPanel';
import { QueueCombobox } from './QueueCombobox';
import { FieldLabel, Section } from './Section';
import { SegmentedControl } from './SegmentedControl';
import { ToastStack } from './ToastStack';
import { ToolCardHeader } from './ToolCardHeader';
import { useOffboardingTool, VALIDATION_COPY } from './useOffboardingTool';
interface OffboardingToolProps {
  /** Pre-selects a departing user as the only source owner. */
  initialSourceUserId?: string;
  /** Context shown between the card header and the first section. */
  banner?: React.ReactNode;
}
export function OffboardingTool({
  initialSourceUserId,
  banner
}: OffboardingToolProps) {
  const tool = useOffboardingTool(initialSourceUserId);
  const {
    prefs,
    counts
  } = tool;
  const sourceOptions: PillOption[] = tool.sourceCandidates.map(u => ({
    id: u.id,
    primary: u.name,
    secondary: u.email,
    tag: prefs.ownerMode === 'PICK_ANY' ? u.active ? 'Active' : 'Inactive' : undefined
  }));
  const targetOptions: PillOption[] = tool.targetCandidates.map(u => ({
    id: u.id,
    primary: u.name,
    secondary: u.email
  }));
  const profileOptions: PillOption[] = profiles.map(p => ({
    id: p.id,
    primary: p.name,
    secondary: `${tool.profileMemberCounts[p.id]} active users`
  }));
  const includesAccounts = prefs.objects.includes('ACCOUNT');
  const includesLeads = prefs.objects.includes('LEAD');
  const objectError = prefs.objects.length === 0;
  return <>
      <section aria-label="User Offboarding Tool" className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-165" data-mp="ju64jb98ti96">
        <ToolCardHeader subtitle="Reassign records from departing users before deactivation" data-id="element-166" data-mp="arn2gtda79nz" />
        {banner && <div className="px-5 pt-5 sm:px-6" data-id="element-167" data-mp="fqgesigu0umk">{banner}</div>}

        <div className={`space-y-6 px-5 py-6 transition-opacity duration-200 sm:px-6 ${tool.busy ? 'pointer-events-none opacity-40' : ''}`} aria-busy={tool.busy} data-id="element-168" data-mp="1355yoc1boq5">
          {/* 1 — Source Owners */}
          <Section number={1} title="Source Owners" icon={UserIcon} data-id="element-169" data-mp="3o95vksahh6v8">
            <FieldLabel data-id="element-170" data-mp="3s8eqi0h9fasd">Owner selection</FieldLabel>
            <SegmentedControl ariaLabel="Owner selection" options={ownerModeOptions} value={prefs.ownerMode} onChange={tool.setOwnerMode} data-id="element-171" data-mp="3s6tsu1znfbqa" />
            {prefs.ownerMode === 'ALL_INACTIVE' ? <p className="mt-3 text-xs text-gray-500" data-id="element-172" data-mp="2znhlh2bwonz7">
                Targets every inactive user who owns Person Accounts or Leads —{' '}
                <span className="font-medium text-gray-700" data-id="element-173" data-mp="2upzn25vilr5g">
                  {tool.sourceOwners.length} owner{tool.sourceOwners.length === 1 ? '' : 's'} found
                </span>
                .
              </p> : <div className="mt-5 max-w-2xl" data-id="element-174" data-mp="3iksj6ihw39cj">
                <PillMultiSelect id="source-owners" label="Select owners" placeholder="Search users by name or email…" helpText="Users whose Person Accounts and Leads will be reassigned." options={sourceOptions} selectedIds={prefs.sourceUserIds} onChange={tool.setSourceUserIds} pillIcon={UserIcon} required data-id="element-175" data-mp="3ijhfeiog21lf" />
              </div>}
          </Section>

          {/* 2 — Objects */}
          <Section number={2} title="Objects to Reassign" icon={LayersIcon} data-id="element-176" data-mp="1un07x71a977u">
            <fieldset aria-describedby="objects-help" data-id="element-177" data-mp="1zm4d7ml36ksw">
              <legend className="mb-2.5 text-[13px] font-medium text-gray-700" data-id="element-178" data-mp="1zkjk81ef79x5">
                Objects to reassign <span className="text-danger" data-id="element-179" data-mp="24fyvqmoygtqe">*</span>
              </legend>
              <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-8" data-id="element-180" data-mp="29bwc2t07y036">
                <ObjectCheckbox label="Accounts (Person Accounts)" count={counts.accounts} checked={includesAccounts} onChange={() => tool.toggleObject('ACCOUNT')} data-id="element-181" data-mp="1bpyzfc8isoz5" />
                <ObjectCheckbox label="Leads" count={counts.leads} checked={includesLeads} onChange={() => tool.toggleObject('LEAD')} data-id="element-182" data-mp="1poqa0oo6bsij" />
              </div>
            </fieldset>
            {objectError ? <p id="objects-help" role="alert" className="mt-2.5 flex items-center gap-1.5 text-xs font-medium text-danger" data-id="element-183" data-mp="315pytabbbp7t">
                <AlertCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-184" data-mp="leeu693s1slh" />
                {VALIDATION_COPY.noObjects}
              </p> : <p id="objects-help" className="mt-2.5 text-xs text-gray-500" data-id="element-185" data-mp="gm3pyu207kn4">
                Select at least one object type.
              </p>}
          </Section>

          {/* 3 — Preview */}
          <Section number={3} title="Preview" icon={BarChart3Icon} data-id="element-186" data-mp="6no0swmjelg9">
            <PreviewPanel accounts={counts.accounts} leads={counts.leads} objects={prefs.objects} ownerCount={tool.sourceOwners.length} awaitingSelection={prefs.ownerMode !== 'ALL_INACTIVE' && prefs.sourceUserIds.length === 0} isRefreshing={tool.isRefreshing} data-id="element-187" data-mp="1pe4ujszngtl" />
          </Section>

          {/* 4 — Reassignment Target */}
          <Section number={4} title="Reassignment Target" icon={RouteIcon} data-id="element-188" data-mp="1n27edc402dbq">
            <FieldLabel data-id="element-189" data-mp="267ca75qfo4v2">Reassignment mode</FieldLabel>
            <SegmentedControl ariaLabel="Reassignment mode" options={reassignModeOptions} value={prefs.reassignMode} onChange={tool.setReassignMode} data-id="element-190" data-mp="2azdk4l04pjhx" />

            <AnimatePresence mode="wait" initial={false} data-id="element-191" data-mp="226iie19ve5ww">
              <motion.div key={prefs.reassignMode} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.15,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-5" data-id="element-192" data-mp="224xtosa8lh6y">
                {prefs.reassignMode === 'USERS' && <div className="max-w-2xl" data-id="element-193" data-mp="2kzw89ryq7m5n">
                    <PillMultiSelect id="target-users" label="Distribute evenly to these active users" placeholder="Search active users…" helpText="Selected users will receive records in round-robin order." options={targetOptions} selectedIds={prefs.targetUserIds} onChange={tool.setTargetUserIds} pillIcon={UserIcon} required data-id="element-194" data-mp="14ja52tk0w35z" />
                  </div>}

                {prefs.reassignMode === 'USERS_BY_PROFILE' && <div className="max-w-2xl" data-id="element-195" data-mp="i91sk4tga84q">
                    <PillMultiSelect id="target-profiles" label="Distribute to active users with these profiles" placeholder="Search profiles…" helpText="All active users with selected profiles will receive records in round-robin order." options={profileOptions} selectedIds={prefs.profileIds} onChange={tool.setProfileIds} pillIcon={BriefcaseIcon} minChars={0} showInitials={false} required data-id="element-196" data-mp="eac5fkim1c7w" />
                  </div>}

                {prefs.reassignMode === 'QUEUE' && (prefs.objects.length === 0 ? <p className="text-sm text-gray-500" data-id="element-197" data-mp="3k55ht24o2olu">
                      Select an object in Objects to Reassign to choose its queue.
                    </p> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-198" data-mp="3t52632n59lgd">
                      {includesAccounts && <QueueCombobox id="account-queue" label="Account Queue" placeholder="Choose Account queue…" options={queues.filter(q => q.object === 'ACCOUNT')} value={prefs.accountQueueId} onChange={tool.setAccountQueueId} required data-id="element-199" data-mp="1s7fiokejjoi" />}
                      {includesLeads && <QueueCombobox id="lead-queue" label="Lead Queue" placeholder="Choose Lead queue…" options={queues.filter(q => q.object === 'LEAD')} value={prefs.leadQueueId} onChange={tool.setLeadQueueId} required data-id="element-200" data-mp="1w5td0yde8exf" />}
                    </div>)}

                {prefs.reassignMode !== 'QUEUE' && tool.recipients.length > 0 && tool.selectedTotal > 0 && <p className="mt-4 text-xs text-gray-600" data-id="element-201" data-mp="255q6reen43zc">
                    <span className="font-medium text-gray-900" data-id="element-202" data-mp="32o8cwgqqdedk">
                      {tool.recipients.length} recipient{tool.recipients.length === 1 ? '' : 's'}
                    </span>{' '}
                    · about {Math.ceil(tool.selectedTotal / tool.recipients.length)} records each
                  </p>}
              </motion.div>
            </AnimatePresence>
          </Section>

          {/* 5 — Exclusions */}
          <Section number={5} title="Exclusions" icon={BanIcon} data-id="element-203" data-mp="58tw1i01ezw">
            <div className="max-w-2xl" data-id="element-204" data-mp="3rcv5cgaymlrb">
              <PillMultiSelect id="excluded-users" label="Exclude users from receiving records" placeholder="Search users to exclude…" helpText="Excluded users will not receive records even if they match the selected profile or user pool." options={targetOptions} selectedIds={prefs.excludedUserIds} onChange={tool.setExcludedUserIds} pillIcon={BanIcon} data-id="element-205" data-mp="3rfzezt7729pd" />
            </div>
          </Section>
        </div>

        {/* 6 — Actions */}
        <footer className="mt-2 flex flex-col gap-4 border-t border-gray-100 px-5 py-5 sm:px-6 md:flex-row md:items-center" data-id="element-206" data-mp="nyr0njbodrze">
          <p className="flex items-start gap-1.5 text-xs md:mr-auto" role="status" data-id="element-207" data-mp="1bkb40qli135x">
            {tool.busy ? null : tool.blocker ? <>
                <InfoIcon className="mt-px h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" data-id="element-208" data-mp="xjql7ufl37cf" />
                <span className="text-gray-500" data-id="element-209" data-mp="12i1ti0q8d9sj">{tool.blocker}</span>
              </> : <>
                <CheckCircle2Icon className="mt-px h-3.5 w-3.5 flex-shrink-0 text-success" aria-hidden="true" data-id="element-210" data-mp="nsm7sz5k3ujp" />
                <span className="text-gray-600" data-id="element-211" data-mp="sn9tyrd38cv4">
                  Ready to reassign {tool.selectedTotal} record{tool.selectedTotal === 1 ? '' : 's'}.
                </span>
              </>}
          </p>
          <div className="flex flex-col gap-3 md:flex-row" data-id="element-212" data-mp="4w4j6qm08aro">
            <button type="button" onClick={tool.savePreferences} disabled={tool.busy} className={neutralButton} data-id="element-213" data-mp="4wmki1nixf6g">
              Save Preferences
            </button>
            <button type="button" onClick={() => tool.openModal('preview')} disabled={!tool.canPreview} className={neutralButton} data-id="element-214" data-mp="3i59yz34hqril">
              Preview Run
            </button>
            <button type="button" onClick={() => tool.openModal('reassign')} disabled={!tool.canReassign} className={`${primaryButton} md:min-w-[120px]`} data-id="element-215" data-mp="388dmynfdvn9a">
              Reassign
            </button>
          </div>
        </footer>

        {tool.busy && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/60" role="status" aria-live="polite" data-id="element-216" data-mp="3vyhp0qcxbey6">
            <Loader2Icon className="h-8 w-8 animate-spin text-navy-600" aria-hidden="true" data-id="element-217" data-mp="2kij8k1ff1qmx" />
            <p className="text-sm font-medium text-gray-700" data-id="element-218" data-mp="2fgtwqjkdd8xs">
              {tool.isInitializing ? 'Loading offboarding data…' : 'Reassigning records…'}
            </p>
          </div>}
      </section>

      <ConfirmModal intent={tool.modalIntent} summary={tool.summary} canConfirm={tool.canReassign} onCancel={tool.closeModal} onConfirm={tool.confirmReassign} onPreviewOnly={tool.previewOnly} data-id="element-219" data-mp="zx4hb0dc4kpa" />
      <ToastStack toasts={tool.toasts} onDismiss={tool.dismissToast} data-id="element-220" data-mp="1eov7giaxn6g5" />
    </>;
}
function ObjectCheckbox({
  label,
  count,
  checked,
  onChange
}: {
  label: string;
  count: number;
  checked: boolean;
  onChange: () => void;
}) {
  return <label className="inline-flex cursor-pointer items-center gap-2.5 text-sm text-gray-900" data-id="element-221" data-mp="1npl23iogiqsa">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-navy-600" data-id="element-222" data-mp="1sn39v5qqmzyp" />
      {label}
      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-gray-600" data-id="element-223" data-mp="3ufhr02lgk0e2">
        {count}
      </span>
    </label>;
}